import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import { Button, Layout, Tour, TourProps, ConfigProvider, theme, Drawer } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import HeaderComponent from './HeaderComponent';
import { useAuth0 } from '@auth0/auth0-react';
import { PermissionsProvider } from './PermissionsProvider';
import ChatComponent from './ChatComponent';
import LeftSiderComponent from './LeftSiderComponent';
import RightSiderComponent from './RightSiderComponent';
import { SandboxApplication } from './model_gen/sandbox';
import { Application } from './model_gen/application';
import { SandboxService } from './services/SandboxService';
import { EvaluateRequest, EvaluateResponse } from './model_gen/evaluation';
import { Sentinel } from './model_gen/sentinel';
import { SandboxMessage } from './model_gen/sandbox';
import SyncChatComponent from './SyncChatComponent';

declare global {
  interface Window {
      heap: any;
  }
}


interface AppLayoutProps {
  children: React.ReactNode;
  chosenApplication: SandboxApplication | undefined;
  setChosenApplication: React.Dispatch<React.SetStateAction<SandboxApplication | undefined>>;
  mappedApplication: Application | undefined;
  loadingApplication: boolean;
  evalResponses: EvaluateResponse[];
  setEvalResponses: React.Dispatch<React.SetStateAction<EvaluateResponse[]>>;
  evalResponsesRef: React.MutableRefObject<EvaluateResponse[]>;
  chooseApplicationRef: React.MutableRefObject<any>;
  considerTourStep: (step: number) => void;
  tripFault: (sentinel: Sentinel) => void;
  models: string[];
  setModels: (models: string[]) => void;
  temperature: number;
  setTemperature: (temperature: number) => void;
  evaluations: boolean;
  setEvaluations: (evaluations: boolean) => void;
  autocorrect: boolean;
  setAutocorrect: (autocorrect: boolean) => void;
  retrieveContext: boolean;
  setRetrieveContext: (retrieveContext: boolean) => void;
  streaming: boolean;
  setStreaming: (streaming: boolean) => void;
  targetMessage: SandboxMessage | null;
  setTargetMessage: React.Dispatch<React.SetStateAction<SandboxMessage | null>>;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, chosenApplication, setChosenApplication, mappedApplication, loadingApplication, evalResponses, setEvalResponses, evalResponsesRef, chooseApplicationRef, considerTourStep, tripFault, models, setModels, temperature, setTemperature, evaluations, setEvaluations, autocorrect, setAutocorrect, retrieveContext, setRetrieveContext, streaming, setStreaming, targetMessage, setTargetMessage }) => {
  const [leftSiderVisible, setLeftSiderVisible] = useState(false);
  const [rightSiderVisible, setRightSiderVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const chatComponentRef = useRef<any>(null);
  
  // Track touch start positions
  const touchStartX = useRef(0);
  const touchThreshold = 50; // Minimum distance to trigger swipe

  // Function to open right sider - will be passed to SyncChatComponent
  const openRightSider = () => {
    if (isMobile) {
      setRightSiderVisible(true);
    }
  };

  // Handle refresh - clear messages and start new session
  const handleRefresh = () => {
    if (chatComponentRef.current?.clearConversation) {
      chatComponentRef.current.clearConversation();
    }
  };

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // On desktop, siders are always visible
      if (!mobile) {
        setLeftSiderVisible(false);
        setRightSiderVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle touch events for swiping to reveal siders
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const screenWidth = window.innerWidth;
      const deltaX = touchEndX - touchStartX.current;
      
      // Right to left swipe near right edge (show right sider)
      if (touchStartX.current > screenWidth - 20 && deltaX < -touchThreshold) {
        setRightSiderVisible(true);
      }
      
      // Left to right swipe near left edge (show left sider)
      if (touchStartX.current < 20 && deltaX > touchThreshold) {
        setLeftSiderVisible(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);
    
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile]);

  // Handler for tapping screen edges
  const handleScreenEdgeTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMobile) return;
    
    const clientX = 'touches' in e 
      ? (e as React.TouchEvent).touches[0].clientX 
      : (e as React.MouseEvent).clientX;
    
    const screenWidth = window.innerWidth;
    
    if (clientX < 20) {
      setLeftSiderVisible(true);
    } else if (clientX > screenWidth - 20) {
      setRightSiderVisible(true);
    }
  };

  return (
    <Layout 
      style={{maxHeight: 'calc(100vh)', height: 'calc(100vh)'}}
      onClick={handleScreenEdgeTap}
      onTouchStart={handleScreenEdgeTap}
    >
      <Header className='Header'>
        <HeaderComponent chooseApplicationRef={chooseApplicationRef} onRefresh={handleRefresh}/>
      </Header>
      <Layout style={{height: '100%'}}>
        {isMobile ? (
          // Mobile layout with drawers
          <>
            <Drawer
              title="Configuration & Sentinels"
              placement="left"
              closable={true}
              onClose={() => setLeftSiderVisible(false)}
              open={leftSiderVisible}
              width="100%"
              styles={{ 
                body: { padding: 0, overflow: "hidden", width: "100%", maxWidth: "500px" },
                header: { padding: "12px 16px" },
                content: { width: "100%", overflow: "hidden", maxWidth: "500px" },
                wrapper: { width: "100%", maxWidth: "500px" }
              }}
            >
              <LeftSiderComponent 
                mappedApplication={mappedApplication} 
                loadingApplication={loadingApplication} 
                tripFault={tripFault} 
                considerTourStep={considerTourStep} 
                models={models} 
                setModels={setModels} 
                temperature={temperature} 
                setTemperature={setTemperature} 
                evaluations={evaluations} 
                setEvaluations={setEvaluations} 
                autocorrect={autocorrect} 
                setAutocorrect={setAutocorrect} 
                retrieveContext={retrieveContext} 
                setRetrieveContext={setRetrieveContext} 
                streaming={streaming} 
                setStreaming={setStreaming}
              />
            </Drawer>
            
            <Drawer
              title="Completion Details"
              placement="right"
              closable={true}
              onClose={() => setRightSiderVisible(false)}
              open={rightSiderVisible}
              width="100%"
              styles={{ 
                body: { padding: 0, overflow: "hidden", width: "100%", maxWidth: "500px" },
                header: { padding: "12px 16px" },
                content: { width: "100%", overflow: "hidden", maxWidth: "500px" },
                wrapper: { width: "100%", maxWidth: "500px" }
              }}
            >
              <RightSiderComponent 
                evalResponses={evalResponses} 
                targetMessage={targetMessage} 
              />
            </Drawer>
            
            {/* Edge indicators for mobile users */}
            <div className="edge-indicator-left" />
            <div className="edge-indicator-right" />
          </>
        ) : (
          // Desktop layout with static siders
          <>
            <LeftSiderComponent 
              mappedApplication={mappedApplication} 
              loadingApplication={loadingApplication} 
              tripFault={tripFault} 
              considerTourStep={considerTourStep} 
              models={models} 
              setModels={setModels} 
              temperature={temperature} 
              setTemperature={setTemperature} 
              evaluations={evaluations} 
              setEvaluations={setEvaluations} 
              autocorrect={autocorrect} 
              setAutocorrect={setAutocorrect} 
              retrieveContext={retrieveContext} 
              setRetrieveContext={setRetrieveContext} 
              streaming={streaming} 
              setStreaming={setStreaming}
            />
          </>
        )}

        <Content style={{ background: '#fff', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {React.cloneElement(children as React.ReactElement, { ref: chatComponentRef, openRightSider })}
        </Content>

        {!isMobile && (
          <RightSiderComponent 
            evalResponses={evalResponses} 
            targetMessage={targetMessage} 
          />
        )}
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  const location = useLocation()
  const { user, isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout } = useAuth0();
  const domain = 'dev-ociu84lf8keizm5i.us.auth0.com';
  const [userMeta, setUserMeta] = useState<any>([]);
  const [chosenApplication, setChosenApplication] = useState<SandboxApplication>();
  const [mappedApplication, setMappedApplication] = useState<Application>();
  const [loadingApplication, setLoadingApplication] = useState(false);
  const [evalResponses, setEvalResponses] = useState<EvaluateResponse[]>([]);
  const evalResponsesRef = useRef<EvaluateResponse[]>([]);
  const chooseApplicationRef = useRef(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepsShown, setTourStepsShown] = useState<number[]>([0]);
  const chatComponentRef = useRef<any>(null);
  const [models, setModels] = useState<string[]>(['maitai:red_robin']);
  const [temperature, setTemperature] = useState(0.4);
  const [evaluations, setEvaluations] = useState(false);
  const [autocorrect, setAutocorrect] = useState(false);
  const [retrieveContext, setRetrieveContext] = useState(false);
  const [streaming, setStreaming] = useState(true);
  const [targetMessage, setTargetMessage] = useState<SandboxMessage | null>(null);
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Maitai Demo',
      description: 'Welcome! In this demo, you\'ll see how Maitai integrates with AI applications to detect and correct faults in the output.\n \
      Sentinels tasked to detect faults will show on the left, and detected faults will show on the right. Start by choosing an application, and then try to coax the application into producing a fault. If you\'re not good enough to break it, you can use the "Trip Fault" buttons.',
      target: () => chooseApplicationRef.current,
      nextButtonProps: {
        children: 'Let\'s go!',
        onClick: () => {
          setTourOpen(false)
        }
      }
    }
  ];

  const considerTourStep = (step: number) => {
    if (!tourStepsShown.includes(step)) {
      window.heap.track('Tour Shown - Welcome');
      setTourOpen(true)
      setTourStepsShown([...tourStepsShown, step]);
    }
  }

  useEffect(() => {
    if (chosenApplication) {
      setTourOpen(false)
    }
  }, [chosenApplication]);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${domain}/api/v2/`,
          scope: "openid profile email read:current_user",
        }
      })
      .then((accessToken) => {
        if (!accessToken) {
          console.error('No access token returned');
          return;
        }
        // Use the access token to fetch user profile from the /userinfo endpoint
        fetch(`https://${domain}/api/v2/users/${user?.sub}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((response) => response.json())
        .then((profile) => {
          // Access user_metadata from the profile
          window.heap.addUserProperties({name: profile?.name})
          setUserMeta(profile['user_metadata'])
          // You can now set this information in your state or use it as needed
        })
        .catch((err) => {
          console.error('Error fetching user profile', err);
          logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
            loginWithRedirect();
        });
        });
      })
      .catch((err) => {
        console.error('Error getting access token', err);
        if (err.error === 'consent_required') {
          logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
              loginWithRedirect();
          });
      }
      });
    } else if (!isLoading) {
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout]);

  useEffect(() => {
    if (!userMeta || !userMeta.api_key) {
      // logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
      //     loginWithRedirect();
      // });
    }
  }, [userMeta, logout]);

  useEffect(() => {
    if (chosenApplication) {
      window.heap.track('Application Chosen', { 'application_name': chosenApplication.sandbox_application_name });
      setLoadingApplication(true);
      SandboxService.getMappedApplication(chosenApplication.id!, setLoadingApplication, userMeta.api_key).subscribe((data: Application) => {
        setMappedApplication(data)
      }, (error: any) => {
        console.log('error', error)
        setLoadingApplication(false)
      });
    }
  }, [chosenApplication])

  useEffect(() => {
    considerTourStep(0)
  }, []);

  const tripFault = (sentinel: Sentinel) => {
    console.log('tripping fault', sentinel)
    if (chatComponentRef.current) {
      chatComponentRef.current.sendFaultyMessage(sentinel)
    }
  }

  // Add effect to fetch and select first application
  useEffect(() => {
    if (userMeta?.api_key && !chosenApplication) {
      SandboxService.getApplications(null, userMeta.api_key).subscribe(
        (data: [SandboxApplication]) => {
          if (data && data.length > 0) {
            const firstApp = data[0];
            setChosenApplication(firstApp);
          }
        },
        (error: any) => {
          console.log("error fetching applications:", error);
        }
      );
    }
  }, [userMeta, chosenApplication]);

  // Define your custom theme
  const customTheme = {
    token: {
      colorPrimary: '#21b892',
      colorSecondary: '#ffa658'
      // Add more color customizations here
    },
  };

  return (
    <ConfigProvider
      theme={customTheme}
    >
      {userMeta && userMeta.api_key !== undefined ? (
        <PermissionsProvider apiKey={userMeta.api_key}>
          <Routes>
            <Route path="*" element={
              <AppLayout chosenApplication={chosenApplication} setChosenApplication={setChosenApplication} mappedApplication={mappedApplication} loadingApplication={loadingApplication} tripFault={tripFault}
                evalResponses={evalResponses} setEvalResponses={setEvalResponses} evalResponsesRef={evalResponsesRef} considerTourStep={considerTourStep} chooseApplicationRef={chooseApplicationRef} 
                models={models} setModels={setModels} temperature={temperature} setTemperature={setTemperature} evaluations={evaluations} setEvaluations={setEvaluations} autocorrect={autocorrect} setAutocorrect={setAutocorrect} retrieveContext={retrieveContext} setRetrieveContext={setRetrieveContext} streaming={streaming} setStreaming={setStreaming} targetMessage={targetMessage} setTargetMessage={setTargetMessage}>
                <SyncChatComponent 
                  chosenApplication={chosenApplication} 
                  mappedApplication={mappedApplication} 
                  evalResponses={evalResponses} 
                  setEvalResponses={setEvalResponses} 
                  evalResponsesRef={evalResponsesRef} 
                  ref={chatComponentRef} 
                  models={models} 
                  temperature={temperature} 
                  evaluations={evaluations} 
                  autocorrect={autocorrect} 
                  retrieveContext={retrieveContext} 
                  streaming={streaming} 
                  targetMessage={targetMessage} 
                  setTargetMessage={setTargetMessage} 
                />
              </AppLayout>
            } />
          </Routes>
        </PermissionsProvider>
      ) : (
        null
      )}
      <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
    </ConfigProvider>
  );
};

export default () => (
  <Router>
    <App />
  </Router>
);
